import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Trash2, X, Users } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useBulkRemoveOrganizationMembers } from "@/hooks/api-hooks";

const bulkDeleteMembersSchema = z.object({
  replacementMemberId: z.string().optional(),
  transferData: z.boolean().default(false),
});

type BulkDeleteMembersFormData = z.infer<typeof bulkDeleteMembersSchema>;

interface Member {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'admin' | 'manager' | 'staff';
  isActive: boolean;
}

interface BulkDeleteMembersModalProps {
  selectedMembers: Member[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (memberIds: string[], options: any) => void;
  organizationId: string;
  replacementMembers: Member[];
}

export function BulkDeleteMembersModal({
  selectedMembers,
  isOpen,
  onClose,
  onConfirm,
  organizationId,
  replacementMembers
}: BulkDeleteMembersModalProps) {
  const bulkRemoveMembersMutation = useBulkRemoveOrganizationMembers();

  const form = useForm<BulkDeleteMembersFormData>({
    resolver: zodResolver(bulkDeleteMembersSchema),
    defaultValues: {
      replacementMemberId: "",
      transferData: false,
    },
  });

  const watchedTransferData = form.watch("transferData");

  const handleBulkDelete = async (data: BulkDeleteMembersFormData) => {
    if (selectedMembers.length === 0) return;

    const memberIds = selectedMembers.map(member => member.id);

    bulkRemoveMembersMutation.mutate(
      {
        organizationId,
        memberIds,
        replacementMemberId: data.replacementMemberId || undefined,
        transferData: data.transferData
      },
      {
        onSuccess: () => {
          toast.success(`${selectedMembers.length} member${selectedMembers.length !== 1 ? 's' : ''} removed from organization`);

          onConfirm?.(memberIds, data);
          onClose();
          form.reset();
        },
        onError: (error: any) => {
          console.error("Error removing members:", error);
          toast.error(`Failed to remove members: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  if (selectedMembers.length === 0) return null;

  const selectedMemberIds = selectedMembers.map(m => m.id);
  const availableReplacements = replacementMembers.filter(m =>
    !selectedMemberIds.includes(m.id) &&
    (m.role === 'admin' || m.role === 'manager')
  );

  const hasManagersOrAdmins = selectedMembers.some(m => m.role === 'admin' || m.role === 'manager');

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'staff':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleCounts = () => {
    const counts = { admin: 0, manager: 0, staff: 0 };
    selectedMembers.forEach(member => {
      counts[member.role]++;
    });
    return counts;
  };

  const roleCounts = getRoleCounts();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            Remove {selectedMembers.length} Team Member{selectedMembers.length !== 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleBulkDelete)} className="space-y-6">
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to remove <strong>{selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''}</strong> from the organization? This action cannot be undone.
              </p>

              <div className="bg-muted/50 rounded-lg p-4 border border-border space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium">Members to be removed:</h4>
                  <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                    {selectedMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between text-sm">
                        <span>{member.user.firstName} {member.user.lastName}</span>
                        <Badge className={getRoleColor(member.role)} variant="secondary">
                          {member.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>Total members:</span>
                      <span className="font-medium">{selectedMembers.length}</span>
                    </div>
                    {roleCounts.admin > 0 && (
                      <div className="flex items-center justify-between">
                        <span>Admins:</span>
                        <span className="font-medium">{roleCounts.admin}</span>
                      </div>
                    )}
                    {roleCounts.manager > 0 && (
                      <div className="flex items-center justify-between">
                        <span>Managers:</span>
                        <span className="font-medium">{roleCounts.manager}</span>
                      </div>
                    )}
                    {roleCounts.staff > 0 && (
                      <div className="flex items-center justify-between">
                        <span>Staff:</span>
                        <span className="font-medium">{roleCounts.staff}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {hasManagersOrAdmins && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="transferData"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Transfer Data & Responsibilities</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Transfer data and responsibilities from admin/manager members to another member
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {watchedTransferData && availableReplacements.length > 0 && (
                    <FormField
                      control={form.control}
                      name="replacementMemberId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Transfer To
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select replacement member" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableReplacements.map((replacement) => (
                                <SelectItem key={replacement.id} value={replacement.user.id}>
                                  <div className="flex items-center gap-2">
                                    <span>{replacement.user.firstName} {replacement.user.lastName}</span>
                                    <Badge variant="secondary" className="text-xs">
                                      {replacement.role}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {watchedTransferData && availableReplacements.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-yellow-800">No Replacement Available</div>
                          <div className="text-sm text-yellow-700">
                            There are no other admins or managers available to transfer data to. The data will be retained but unassigned.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-red-800">Warning</div>
                    <div className="text-sm text-red-700">
                      Removing these members will revoke their access to the organization immediately. This action cannot be undone.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={bulkRemoveMembersMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={bulkRemoveMembersMutation.isPending}
                className="gap-2"
              >
                {bulkRemoveMembersMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-background"></div>
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Remove {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
